import { Fragment, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Radio from 'components/CncUI/Radio';
import TextField from 'components/CncUI/TextField';
import TextFieldEmail from 'components/CncUI/TextField/TextFieldEmail';
import SupportFooter from 'components/SupportFooter';
import NotPaidModal from '../modals/NotPaidModal';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import BackIcon from '@material-ui/icons/ChevronLeft';

import useFeatureFlags from 'hooks/useFeatureFlags';
import validateEmailRule from 'utilities/validateEmailRule';
import cncLogo from 'images/CnCLogoRegister.svg';

const FLAG_MINORS = 'MyCnC-Minors';

const useStyles = makeStyles(({ palette, breakpoints, spacing }) => ({
  root: {
    maxWidth: 1180,
    margin: 'auto',
    [breakpoints.up('md')]: {
      paddingTop: 88,
    },
  },
  leftPanel: {
    backgroundImage: palette.cncUI.gradients.header,
    boxShadow:
      '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
    [breakpoints.up('md')]: {
      height: 630,
      boxShadow: '5px 2px 8px rgba(0, 0, 0, 0.2);',
      zIndex: 100,
    },
    [breakpoints.only('xs')]: {
      height: 'auto',
      paddingBottom: 24,
    },
  },
  rightPanel: {
    padding: '6px 0',
    [breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  paper: {
    borderRadius: 0,
    padding: '16px 16px 24px',
    overflowY: 'auto',
    [breakpoints.up('md')]: {
      height: 600,
      padding: '16px 95px 0',
    },
    '& h4': {
      [breakpoints.up('md')]: {
        marginTop: 150,
      },
      [breakpoints.down('sm')]: {
        fontWeight: 400,
      },
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 16,
      lineHeight: '22px',
      fontWeight: 400,
    },
    '& .cncui-radio-label': {
      marginLeft: -8,
      marginBottom: 8,
      [breakpoints.down('sm')]: {
        marginBottom: 20,
      },
    },
  },
  innerLeft: {
    height: '100%',
    color: palette.cncUI.default['+10'],
    padding: '8px 16px',
    [breakpoints.up('md')]: {
      padding: 40,
    },
    '& h3': {
      [breakpoints.up('md')]: {
        marginTop: 60,
      },
      [breakpoints.down('sm')]: {
        textAlign: 'center',
        marginTop: 20,
      },
    },
    '& p': {
      [breakpoints.down('sm')]: {
        textAlign: 'center',
        marginTop: 20,
      },
    },
    '& h4': {
      fontSize: 16,
      fontWeight: 700,
    },
  },
  logo: {
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
    '& img': {
      [breakpoints.up('sm')]: {
        marginLeft: 2,
        width: 340,
      },
      [breakpoints.down('sm')]: {
        width: 280,
        marginTop: 40,
      },
    },
  },
  hiddenLink: {
    display: 'none',
    textAlign: 'center',
    margin: '24px 0',
    [breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  withIcon: {
    color: palette.cncUI.white,
    textDecoration: 'none',
    marginBottom: 8,
    display: 'block',
    fontSize: 14,
    fontWeight: 400,
    '& svg': {
      backgroundColor: palette.cncUI.default.background,
      borderRadius: '50%',
      color: palette.cncUI.default['-3'],
      verticalAlign: 'middle',
      marginRight: 8,
    },
    '&:hover': {
      textDecoration: 'underline',
    },
    [breakpoints.down('sm')]: {
      color: palette.cncUI.default['+0'],
    },
  },
  hidden: {
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  group: {
    [breakpoints.up('md')]: {
      marginTop: 16,
    },
    [breakpoints.down('sm')]: {
      marginTop: 16,
    },
  },
  button: {
    [breakpoints.up('md')]: {
      marginTop: 56,
    },
    [breakpoints.down('sm')]: {
      marginTop: 24,
      marginBottom: 15,
    },
  },
  noHeight: {
    height: 0,
  },
  mobileExpand: {
    position: 'relative',
    backgroundColor: palette.cncUI.default['+6'],
    color: palette.cncUI.white,
    minHeight: 24,
    width: 24,
    height: 24,
    alignContent: 'center',
    justifyContent: 'center',
    top: -12,
    borderRadius: '50%',
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  radio: {
    color: palette.cncUI.default['-5'],
    '&$checked': {
      backgroundColor: palette.cncUI.default['+10'],
      color: palette.cncUI.default.primary,
    },
  },
  radioLabel: {
    color: palette.text.primary,
    '& span': {
      fontSize: 14,
    },
    [breakpoints.down('sm')]: {
      marginBottom: 12,
    },
  },
  subtitle: {
    [breakpoints.only('sm')]: {
      maxWidth: 605,
      margin: '16px auto',
    },
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  paperWrapper: {
    [breakpoints.only('sm')]: {
      maxWidth: 605,
      marginLeft: 'auto',
      marginRight: 'auto',
      marginTop: 24,
    },
  },
  formTitle: {
    marginBottom: spacing(4),
  },
  buttonContainer: {
    marginTop: spacing(3),
  },
}));

const Welcome = props => {
  const classes = useStyles();
  const { isEnabled } = useFeatureFlags();
  const { setStep, changeState, isMobile, state: stepState } = props;
  const [state, setState] = useState({
    paid: '',
    show: false,
  });
  const [showTokenForm, setTokenForm] = useState(false);
  const { lockedEmail = false } = stepState;
  const minorsEnabled = isEnabled(FLAG_MINORS);
  const change = value => {
    setState({ show: ['false', 'maybe'].includes(value), paid: value });
    changeState({ hasBeenPaid: value });
  };

  const {
    formState,
    handleSubmit,
    reset,
    register,
    control,
    formState: { errors },
  } = useForm({
    mode: 'all',
    defaultValues: {
      email: '',
      token: '',
    },
  });

  const enableTokenAccess = () => {
    setTokenForm(true);
  };

  const cancelToken = () => {
    reset();
    setTokenForm(false);
  };

  const onSubmit = async data => {
    window.location.replace(
      `welcome/${data.token}?login=${encodeURIComponent(data.email)}`,
    );
  };

  const fieldRequired = {
    required: 'This field is required',
  };

  const validateEmail = {
    validate: email => validateEmailRule(email) || 'Type a valid email address',
  };

  const tokenFormat = {
    pattern: {
      value: /^[A-Za-z0-9-_]{20}$/,
      message: 'Token code format is invalid',
    },
  };

  return (
    <Fragment>
      <NotPaidModal
        show={state.show}
        onClose={() => setState({ ...state, show: false })}
        type={state.paid}
        setStep={setStep}
        isMobile={isMobile}
      />
      <Grid container spacing={0} className={classes.root} id="Welcome">
        <Grid item xs={12} md={6} className={classes.leftPanel}>
          <Grid
            container
            spacing={0}
            justifyContent="space-between"
            direction="column"
            className={classes.innerLeft}
          >
            <Grid item>
              <Grid container spacing={0} justifyContent="center">
                <Grid item xs={12} sm={8} md={12}>
                  <div className={classes.logo}>
                    <img src={cncLogo} alt="CastandCrew Logo" />
                  </div>
                  <Typography variant="h3" white>
                    Registration
                  </Typography>
                  <Typography variant="h5" white className={classes.subtitle}>
                    Tell us about yourself so that Cast & Crew can find and
                    securely retrieve your data and link it to your new
                    MyCast&Crew account.
                  </Typography>
                  {!showTokenForm && (
                    <Typography variant="h5" white>
                      If you have a token code,{' '}
                      <a
                        href="#"
                        onClick={enableTokenAccess}
                        style={{ color: 'white' }}
                      >
                        click here
                      </a>
                      .
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item className={classes.hidden}>
              <Link
                className={classes.withIcon}
                to="/login"
                id="WelcomeToLoginLink"
              >
                <BackIcon /> Back to {lockedEmail ? 'dashboard' : 'login page'}
              </Link>
            </Grid>
          </Grid>
        </Grid>
        {isMobile && (
          <Grid item xs={12} className={classes.noHeight}>
            <div className={classes.mobileExpand}>
              <ExpandMoreIcon />
            </div>
          </Grid>
        )}
        <Grid item xs={12} md={6} className={classes.rightPanel}>
          <Paper className={classes.paper}>
            {!showTokenForm && (
              <Grid
                container
                spacing={0}
                className={classes.paperWrapper}
                justifyContent="center"
              >
                <Grid item xs={12} sm={10} md={12}>
                  <Typography variant="h4">
                    {minorsEnabled
                      ? 'Have you or your minor(s) ever been paid by Cast & Crew, CAPS, or Media Services?'
                      : 'Have you ever been paid by Cast & Crew, CAPS, or Media Services?'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={12}>
                  <Radio.Group
                    aria-label="Have been paid"
                    name="paid"
                    id="WelcomeRadioGroupPaid"
                    className={classes.group}
                    value={state.paid}
                    onChange={e => change(e.target.value)}
                  >
                    <Radio
                      value="true"
                      size="short"
                      id="WelcomeRadioPaidYes"
                      labelPosition="right"
                    >
                      YES, I have
                    </Radio>
                    <Radio
                      value="false"
                      size="short"
                      id="WelcomeRadioPaidNo"
                      labelPosition="right"
                    >
                      NO, I have not
                    </Radio>
                    <Radio
                      value="maybe"
                      size="short"
                      id="WelcomeRadioPaidMaybe"
                      labelPosition="right"
                    >
                      I don't remember
                    </Radio>
                  </Radio.Group>
                </Grid>
                <Grid item xs={12} sm={10} md={12}>
                  <Button
                    fullWidth
                    onClick={() => setStep('FindAccount')}
                    id="WelcomeNextButton"
                    className={classes.button}
                    disabled={!['true', 'maybe'].includes(state.paid)}
                  >
                    Next
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={10}
                  md={12}
                  className={classes.hiddenLink}
                >
                  <Link
                    className={classes.withIcon}
                    to="/login"
                    id="WelcomeToLoginLink"
                  >
                    <BackIcon /> Back to{' '}
                    {lockedEmail ? 'dashboard' : 'login page'}
                  </Link>
                </Grid>
              </Grid>
            )}
            {showTokenForm && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  spacing={0}
                  className={classes.paperWrapper}
                  justifyContent="center"
                >
                  <Grid item xs={12} sm={10} md={12}>
                    <Typography variant="h4" className={classes.formTitle}>
                      Have a token code? <br /> submit your token code here.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={10} md={12} container spacing={1}>
                    <Grid item xs={12}>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          ...fieldRequired,
                          ...validateEmail,
                        }}
                        render={({ field: { value, onBlur, onChange } }) => (
                          <TextFieldEmail
                            id="WelcomeRegister-Email"
                            label="Email *"
                            value={value}
                            onChange={e => {
                              onChange(e.currentTarget.value);
                            }}
                            onBlur={onBlur}
                            error={!!errors?.email}
                            helperText={errors?.email?.message || ' '}
                            autoComplete="off"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="token"
                        fullWidth
                        label="Token code*"
                        InputProps={{
                          ...register('token', {
                            ...fieldRequired,
                            ...tokenFormat,
                          }),
                        }}
                        error={!!errors?.token}
                        helperText={errors?.token?.message || ' '}
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={10}
                    md={12}
                    container
                    spacing={1}
                    className={classes.buttonContainer}
                  >
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        id="WelcomeTokenCancelButton"
                        secondary
                        onClick={cancelToken}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        type="submit"
                        id="WelcomeTokenSubmitButton"
                        disabled={!formState.isValid}
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={10}
                    md={12}
                    className={classes.hiddenLink}
                  >
                    <Link
                      className={classes.withIcon}
                      to="/login"
                      id="WelcomeToLoginLink"
                    >
                      <BackIcon /> Back to{' '}
                      {lockedEmail ? 'dashboard' : 'login page'}
                    </Link>
                  </Grid>
                </Grid>
              </form>
            )}
          </Paper>
        </Grid>
      </Grid>
      <SupportFooter />
    </Fragment>
  );
};

export default Welcome;
